import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles, ChevronLeft, ChevronRight, Check, TrendingUp, LifeBuoy, Receipt,
  Users, Inbox, Plus, Wand2, Clock, Globe2,
} from "lucide-react";

const PRESET_ICONS = {
  "trending-up": TrendingUp, "life-buoy": LifeBuoy, receipt: Receipt, users: Users, inbox: Inbox,
};

const ACCENT_CLS = {
  emerald: "border-emerald-brand/40 bg-emerald-brand/8 text-emerald-brand",
  sky: "border-sky-500/40 bg-sky-500/10 text-sky-600",
  amber: "border-yellow-500/40 bg-yellow-500/10 text-yellow-700",
  rose: "border-rose-500/40 bg-rose-500/10 text-rose-600",
  slate: "border-slate-400/40 bg-slate-400/10 text-slate-600",
};

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const SCHEDULE_TEMPLATES = [
  { key: "always", label: "Always on (24/7)", days: [], hStart: 0, hEnd: 24 },
  { key: "business", label: "Business hours (Mon–Fri, 09–18)", days: [0, 1, 2, 3, 4], hStart: 9, hEnd: 18 },
  { key: "afterhours", label: "After hours only (Mon–Fri, 18–09)", days: [0, 1, 2, 3, 4], hStart: 18, hEnd: 24 },
  { key: "weekends", label: "Weekends only (Sat–Sun)", days: [5, 6], hStart: 0, hEnd: 24 },
  { key: "custom", label: "Custom", days: [], hStart: 9, hEnd: 18 },
];

/**
 * AIAgentWizard — 3-step wizard for assigning an AI Agent + auto-reply to
 * an email account, by department. Pre-fills prompt & message from the
 * chosen department (or curated preset).
 *
 * Props: account (email account row), open, onClose, onSaved (callback), companyId
 */
export default function AIAgentWizard({ account, open, onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [presets, setPresets] = useState([]);
  const [depts, setDepts] = useState([]);
  const [selectedDeptId, setSelectedDeptId] = useState("");
  const [pendingPresetKey, setPendingPresetKey] = useState("");
  const [busy, setBusy] = useState(false);

  // Step 2 state
  const [tpl, setTpl] = useState("always");
  const [days, setDays] = useState([]);
  const [hStart, setHStart] = useState(0);
  const [hEnd, setHEnd] = useState(24);
  const [tz, setTz] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
  const [autoSend, setAutoSend] = useState(false);

  // Step 3 state
  const [aiEnabled, setAiEnabled] = useState(true);
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const [aiPrompt, setAiPrompt] = useState("");
  const [savingPrompt, setSavingPrompt] = useState(false);

  const companyId = account?.company_id;

  useEffect(() => {
    if (!open || !account) return;
    setStep(1);
    setPendingPresetKey("");
    (async () => {
      const [p, d] = await Promise.all([
        api.get("/departments/presets"),
        api.get("/departments", { params: { company_id: companyId } }),
      ]);
      setPresets(p.data);
      setDepts(d.data.filter((x) => x.company_id === companyId && x.active !== false));
      // Preselect current department if account has one
      const cur = (account.department_ids || [])[0] || "";
      setSelectedDeptId(cur);
    })();
    // Prime step 2 from account
    setDays(account.ai_auto_reply_days || []);
    setHStart(account.ai_auto_reply_hour_start ?? 0);
    setHEnd(account.ai_auto_reply_hour_end ?? 24);
    setTz(account.ai_auto_reply_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC");
    setAutoSend(!!account.auto_send_enabled);
    setAiEnabled(account.ai_enabled !== false);
    setAutoReplyEnabled(!!account.ai_auto_reply_enabled);
    setMessage(account.ai_auto_reply_message || "");
    setAiPrompt("");
    // eslint-disable-next-line
  }, [open, account?.id]);

  const chosenDept = useMemo(() => depts.find((d) => d.id === selectedDeptId), [depts, selectedDeptId]);

  // When user selects a dept, prefill message + prompt (Step 3 previews)
  useEffect(() => {
    if (chosenDept) {
      setAiPrompt(chosenDept.ai_system_prompt || "");
      if (!message) setMessage(chosenDept.auto_reply_text || "");
    }
    // eslint-disable-next-line
  }, [selectedDeptId]);

  const createFromPreset = async (preset) => {
    setBusy(true);
    try {
      const { data } = await api.post("/departments/from-preset", {
        company_id: companyId, preset_key: preset.key,
      });
      toast.success(`Department "${data.name}" created`);
      setDepts((prev) => [data, ...prev]);
      setSelectedDeptId(data.id);
      setPendingPresetKey("");
      setAiPrompt(data.ai_system_prompt || "");
      setMessage(data.auto_reply_text || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create department");
    }
    setBusy(false);
  };

  const applyTemplate = (key) => {
    setTpl(key);
    const t = SCHEDULE_TEMPLATES.find((x) => x.key === key);
    if (!t || key === "custom") return;
    setDays(t.days);
    setHStart(t.hStart);
    setHEnd(t.hEnd);
  };

  const canProceed = step === 1 ? !!selectedDeptId : step === 2 ? true : true;

  const save = async () => {
    setBusy(true);
    try {
      const patch = {
        department_ids: selectedDeptId ? [selectedDeptId] : [],
        ai_enabled: aiEnabled,
        auto_send_enabled: autoSend,
        ai_auto_reply_enabled: autoReplyEnabled,
        ai_auto_reply_message: message,
        ai_auto_reply_days: days,
        ai_auto_reply_hour_start: hStart,
        ai_auto_reply_hour_end: hEnd,
        ai_auto_reply_timezone: tz,
      };
      await api.patch(`/email-accounts/${account.id}`, patch);
      // If the AI prompt was edited AND we have a department, persist it there
      if (chosenDept && aiPrompt !== (chosenDept.ai_system_prompt || "")) {
        setSavingPrompt(true);
        await api.patch(`/departments/${chosenDept.id}`, { ai_system_prompt: aiPrompt });
        setSavingPrompt(false);
      }
      toast.success("AI Agent configured");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
    setBusy(false);
  };

  if (!account) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid="ai-wizard-modal">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-terracotta" /> Set up AI Agent
            <Badge variant="outline" className="rounded-sm font-mono text-[10px] ml-2">
              {account.email_address}
            </Badge>
          </DialogTitle>
          <div className="mt-3">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
              <StepPill n={1} label="Department" active={step === 1} done={step > 1} />
              <div className="flex-1 h-px bg-border" />
              <StepPill n={2} label="Schedule" active={step === 2} done={step > 2} />
              <div className="flex-1 h-px bg-border" />
              <StepPill n={3} label="Review" active={step === 3} done={false} />
            </div>
            <Progress value={step === 1 ? 33 : step === 2 ? 66 : 100} className="mt-3 h-1 rounded-sm" />
          </div>
        </DialogHeader>

        <div className="px-6 pb-4 space-y-4">
          {/* Step 1: Department selection */}
          {step === 1 && (
            <div className="space-y-4" data-testid="wizard-step-1">
              <p className="text-sm text-muted-foreground">
                Pick a department to give your AI agent context, tone, and a starter auto-reply message.
              </p>

              {depts.length > 0 && (
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                    Existing departments in this company
                  </Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                    {depts.map((d) => {
                      const active = selectedDeptId === d.id;
                      return (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => setSelectedDeptId(d.id)}
                          className={`text-left border rounded-sm p-3 transition-all ${active ? "border-emerald-brand bg-emerald-brand/8 shadow-sm" : "border-border hover:border-emerald-brand/40 hover:bg-muted/40"}`}
                          data-testid={`wizard-dept-${d.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="font-medium">{d.name}</div>
                            {active && <Check className="h-4 w-4 text-emerald-brand" />}
                          </div>
                          <div className="text-[11px] font-mono text-muted-foreground uppercase mt-0.5">{d.dept_type}</div>
                          {d.ai_system_prompt && (
                            <div className="text-xs text-muted-foreground mt-1.5 line-clamp-2">
                              {d.ai_system_prompt.slice(0, 140)}{d.ai_system_prompt.length > 140 ? "…" : ""}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                  <Wand2 className="h-3 w-3" /> Or start from a curated preset
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-2">
                  {presets.map((p) => {
                    const Icon = PRESET_ICONS[p.icon] || Inbox;
                    const cls = ACCENT_CLS[p.accent] || ACCENT_CLS.slate;
                    const alreadyExists = depts.some((d) => (d.code || "").toUpperCase() === (p.code || "").toUpperCase() && d.company_id === companyId);
                    return (
                      <button
                        key={p.key}
                        type="button"
                        disabled={busy || alreadyExists}
                        onClick={() => createFromPreset(p)}
                        className={`text-left border rounded-sm p-3 transition-all ${alreadyExists ? "opacity-50 cursor-not-allowed" : "hover:shadow-md hover:-translate-y-0.5"} ${cls}`}
                        data-testid={`wizard-preset-${p.key}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-sm bg-white/40 grid place-items-center backdrop-blur-sm">
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-foreground text-sm">{p.name}</div>
                            <div className="text-[10px] font-mono uppercase text-muted-foreground">{p.dept_type}</div>
                          </div>
                          {alreadyExists ? (
                            <Check className="h-3.5 w-3.5" />
                          ) : (
                            <Plus className="h-3.5 w-3.5" />
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-2 line-clamp-2">{p.description}</div>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">
                  Tip: clicking a preset creates a full department (prompt + auto-reply text) in one click and selects it here.
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Schedule */}
          {step === 2 && (
            <div className="space-y-4" data-testid="wizard-step-2">
              <p className="text-sm text-muted-foreground">
                When should the AI auto-reply fire? Pick a preset or fine-tune below.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                {SCHEDULE_TEMPLATES.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => applyTemplate(t.key)}
                    className={`text-left border rounded-sm p-2.5 text-xs transition-all ${tpl === t.key ? "border-emerald-brand bg-emerald-brand/8" : "border-border hover:border-emerald-brand/40"}`}
                    data-testid={`wizard-schedule-${t.key}`}
                  >
                    <div className="flex items-center gap-1.5 font-medium">
                      <Clock className="h-3 w-3" /> {t.label.split("(")[0].trim()}
                    </div>
                    <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
                      {t.label.includes("(") ? t.label.match(/\(([^)]+)\)/)[1] : "your rules"}
                    </div>
                  </button>
                ))}
              </div>

              <div className="rounded-sm border border-border p-3 space-y-3 bg-muted/20">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Custom window</div>
                <div>
                  <Label className="text-[11px] text-muted-foreground">Active days (empty = every day)</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {DAY_LABELS.map((d, i) => {
                      const on = days.length === 0 || days.includes(i);
                      return (
                        <button
                          key={d}
                          type="button"
                          onClick={() => {
                            const cur = new Set(days);
                            if (cur.has(i)) cur.delete(i); else cur.add(i);
                            setDays(Array.from(cur).sort());
                            setTpl("custom");
                          }}
                          className={`px-2.5 py-1 rounded-sm text-xs font-mono border transition-colors ${on ? "bg-emerald-brand/12 text-emerald-brand border-emerald-brand/30" : "border-border text-muted-foreground hover:bg-muted"}`}
                          data-testid={`wizard-day-${i}`}
                        >{d}</button>
                      );
                    })}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <FieldMini label="Hour start (0–23)">
                    <Input type="number" min={0} max={23} value={hStart} onChange={(e) => { setHStart(Number(e.target.value)); setTpl("custom"); }} data-testid="wizard-hour-start" />
                  </FieldMini>
                  <FieldMini label="Hour end (1–24)">
                    <Input type="number" min={1} max={24} value={hEnd} onChange={(e) => { setHEnd(Number(e.target.value)); setTpl("custom"); }} data-testid="wizard-hour-end" />
                  </FieldMini>
                  <FieldMini label="Timezone">
                    <div className="relative">
                      <Globe2 className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input className="pl-7" value={tz} onChange={(e) => setTz(e.target.value)} placeholder="America/New_York" data-testid="wizard-tz" />
                    </div>
                  </FieldMini>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <ToggleRow label="AI enabled" hint="Master switch for AI features on this mailbox." checked={aiEnabled} onChange={setAiEnabled} testid="wizard-ai-enabled" />
                <ToggleRow
                  label="Auto-send drafts"
                  hint={chosenDept?.ai_mode === "approval" ? "Disabled: this department requires human approval." : "Send AI replies without human approval when confident."}
                  checked={autoSend && chosenDept?.ai_mode !== "approval"}
                  onChange={(v) => setAutoSend(v && chosenDept?.ai_mode !== "approval")}
                  disabled={chosenDept?.ai_mode === "approval"}
                  testid="wizard-auto-send"
                />
              </div>
              <ToggleRow label="Enable auto-reply" hint="Fire a reply automatically inside the window above." checked={autoReplyEnabled} onChange={setAutoReplyEnabled} testid="wizard-autoreply-enabled" />
            </div>
          )}

          {/* Step 3: Review & customise */}
          {step === 3 && (
            <div className="space-y-4" data-testid="wizard-step-3">
              <p className="text-sm text-muted-foreground">
                Review your AI Agent. The prompt is shared with everyone in the department; the message
                is the auto-reply sent inside the schedule window.
              </p>
              <div className="rounded-sm border border-border p-3 bg-muted/20 space-y-1 text-xs">
                <Row k="Mailbox" v={account.email_address} />
                <Row k="Department" v={chosenDept?.name || "—"} />
                <Row k="AI mode" v={(chosenDept?.ai_mode || "draft").toUpperCase()} />
                <Row k="Auto-reply" v={autoReplyEnabled ? "ON" : "OFF"} />
                <Row k="Window" v={`${days.length ? days.map((i) => DAY_LABELS[i]).join(",") : "every day"} · ${String(hStart).padStart(2, "0")}:00–${String(hEnd).padStart(2, "0")}:00 ${tz}`} />
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  Department AI prompt (persona / tone / boundaries)
                </Label>
                <Textarea rows={4} className="rounded-sm mt-1.5 font-mono text-xs" value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} data-testid="wizard-ai-prompt" />
                <div className="text-[10px] text-muted-foreground mt-1">
                  {savingPrompt ? "Saving…" : chosenDept ? `Applies to all mailboxes in ${chosenDept.name}` : "Select a department first"}
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  Auto-reply message (leave empty to let AI generate every time)
                </Label>
                <Textarea rows={5} className="rounded-sm mt-1.5" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Hi, thanks for your email…" data-testid="wizard-message" />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="p-6 pt-0 flex justify-between sm:justify-between">
          <div>
            {step > 1 && (
              <Button variant="outline" className="rounded-sm" onClick={() => setStep(step - 1)} data-testid="wizard-back">
                <ChevronLeft className="h-4 w-4 mr-1" /> Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" className="rounded-sm" onClick={() => onClose?.()} data-testid="wizard-cancel">Cancel</Button>
            {step < 3 && (
              <Button className="rounded-sm" disabled={!canProceed} onClick={() => setStep(step + 1)} data-testid="wizard-next">
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            {step === 3 && (
              <Button className="rounded-sm bg-emerald-brand hover:bg-emerald-brand/90" disabled={busy || !selectedDeptId} onClick={save} data-testid="wizard-save">
                <Check className="h-4 w-4 mr-1" /> Save & activate
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StepPill({ n, label, active, done }) {
  return (
    <div className="flex items-center gap-1.5">
      <div className={`h-5 w-5 rounded-full grid place-items-center text-[10px] font-mono border transition-colors ${done ? "bg-emerald-brand text-white border-emerald-brand" : active ? "bg-emerald-brand/10 text-emerald-brand border-emerald-brand" : "border-border text-muted-foreground"}`}>
        {done ? <Check className="h-3 w-3" /> : n}
      </div>
      <span className={`${active ? "text-emerald-brand" : done ? "text-emerald-brand/70" : "text-muted-foreground"}`}>{label}</span>
    </div>
  );
}

function FieldMini({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function ToggleRow({ label, hint, checked, onChange, testid, disabled }) {
  return (
    <div className={`flex items-center justify-between rounded-sm border border-border px-3 py-2 ${disabled ? "opacity-60" : ""}`}>
      <div className="pr-3">
        <div className="text-sm font-medium">{label}</div>
        {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testid} />
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <div className="text-muted-foreground uppercase font-mono text-[10px] tracking-widest">{k}</div>
      <div className="font-mono text-foreground text-right break-all">{v}</div>
    </div>
  );
}
